import { createFileRoute } from "@tanstack/react-router";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { breadcrumbs, faqPage, speakable, aeoGeo } from "@/lib/seo/schema";

const FAQ: LandingFAQ[] = [
  {
    q: "Har ni lediga jobb inom film just nu?",
    a: "Vi rekryterar löpande och annonserar när en roll öppnar. Har vi ingen utlyst tjänst tar vi ändå emot spontanansökningar — de flesta som börjat hos oss hörde av sig innan en annons fanns.",
  },
  {
    q: "Vilka roller finns på en filmbyrå?",
    a: "Producent, projektledare, regissör, filmfotograf, ljudtekniker, redigerare, colorist, motion designer och strateg. Hos oss ligger hela kedjan inhouse, vilket innebär att de flesta arbetar i mer än en roll under ett projekt.",
  },
  {
    q: "Vad krävs för att jobba med filmproduktion?",
    a: "Portfolio väger tyngre än examen. Visa fem klipp du gjort själv och beskriv vad du ansvarade för i varje. Vi tittar på bildspråk, klippkänsla, ljudkvalitet och om filmen faktiskt löser en uppgift.",
  },
  {
    q: "Tar ni emot praktikanter?",
    a: "Ja. Vi tar emot praktik från yrkeshögskola, universitet och gymnasiets medieprogram i mån av plats. Läs mer på sidan om praktik inom filmproduktion.",
  },
  {
    q: "Var är ni baserade?",
    a: "Vi producerar i hela Sverige med bas i Stockholm, Göteborg och Malmö. Produktionsarbete sker där uppdraget är, planering och post kan ske delvis på distans.",
  },
  {
    q: "Hur ansöker jag?",
    a: "Skicka en kort presentation, länk till portfolio och vilken roll du siktar på via kontaktformuläret. Vi svarar på alla ansökningar, även när vi inte har en plats just då.",
  },
];

export const Route = createFileRoute("/jobb")({
  head: () => ({
    meta: [
      { title: "Jobb inom film — jobba på Fenny | Karriär" },
      {
        name: "description",
        content:
          "Jobb inom film och filmproduktion på Fenny. Roller, vad vi tittar på i portfolion och hur du skickar en spontanansökan till vårt inhouse-team.",
      },
      { property: "og:title", content: "Jobb inom film — jobba på Fenny" },
      {
        property: "og:description",
        content:
          "Roller på en filmbyrå, vad vi värderar i en ansökan och hur du hör av dig.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/jobb" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/jobb" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/jobb", name: "Jobb inom film — jobba på Fenny | Karriär", description: "Jobb inom film och filmproduktion på Fenny. Roller, vad vi tittar på i portfolion och hur du skickar en spontanansökan till vårt inhouse-team."})) },
      { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Jobb", path: "/jobb" },
          ]),
        ),
      },
    ],
  }),
  component: JobbPage,
});

function JobbPage() {
  return (
    <LandingPage
      eyebrow="Karriär"
      title={<>Jobba med film hos Fenny.</>}
      lede="Vi bygger ett inhouse-team i stället för att sätta ihop en frilanskedja per projekt. Det betyder färre men bredare roller: du är med från brief till leverans och ser vad filmen faktiskt gör för kunden. Vi rekryterar löpande och läser alla spontanansökningar."
      tldr={
        <p>
          Fenny är en filmbyrå med eget produktionsteam i Stockholm, Göteborg
          och Malmö. Vi rekryterar löpande inom produktion, foto, klipp, motion
          och projektledning. <strong>Portfolio väger tyngre än examen</strong> —
          skicka fem klipp och vad du gjorde i dem.
        </p>
      }
      ctaPrimary={{ label: "Skicka ansökan", to: "/kontakt" }}
      ctaSecondary={{ label: "Om Fenny", to: "/om-fenny" }}
      trackingId="jobb"
      bullets={[
        { title: "Hela kedjan inhouse", body: "Du jobbar med strategi, produktion och post — inte bara ett steg." },
        { title: "Riktiga uppdrag direkt", body: "Vi kastar inte in nyanställda i arkivarbete. Du är med på inspelning tidigt." },
        { title: "Tre orter", body: "Stockholm, Göteborg och Malmö. Produktion sker där uppdraget är." },
      ]}
      sections={[
        {
          id: "roller",
          heading: "Roller vi rekryterar till",
          body: (
            <>
              <p>
                Producent och projektledare, regissör, filmfotograf,
                ljudtekniker, redigerare, colorist, motion designer och
                strateg. Eftersom vi håller hela kedjan inhouse arbetar de
                flesta i mer än en roll — en redigerare hos oss är ofta med på
                inspelning, och en fotograf är med när konceptet sätts.
              </p>
              <p>
                Vill du veta vad rollerna gör i praktiken, läs om{" "}
                <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/filmbolag">
                  hur ett filmbolag arbetar
                </a>{" "}
                och vår{" "}
                <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/process">
                  produktionsprocess
                </a>
                .
              </p>
            </>
          ),
        },
        {
          id: "ansokan",
          heading: "Vad vi tittar på i en ansökan",
          body: (
            <>
              <p>
                Fem klipp räcker. Skriv vad du ansvarade för i varje — regi,
                foto, ljud, klipp eller grade — och vad filmen skulle
                åstadkomma. Vi tittar på bildspråk, klipprytm, ljudkvalitet och
                om filmen löser en faktisk uppgift. En snygg reel utan uppdrag
                bakom säger mindre än ett enkelt kundcase som fungerade.
              </p>
              <p>
                Skriv också var du är baserad och hur du ser på resande
                produktion. Vi arbetar i hela Sverige.
              </p>
            </>
          ),
        },
        {
          id: "praktik",
          heading: "Praktik och examensarbete",
          body: (
            <p>
              Vi tar emot praktikanter från yrkeshögskola, universitet och
              gymnasiets medieprogram i mån av plats. Detaljer, perioder och vad
              en praktikperiod innehåller finns på sidan om{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/praktik">
                praktik inom filmproduktion
              </a>
              .
            </p>
          ),
        },
        {
          id: "frilans",
          heading: "Frilansare och underleverantörer",
          body: (
            <p>
              Vid större produktioner kompletterar vi teamet med frilansare
              inom ljus, ljud, grip och styling. Är du frilans och vill finnas i
              vårt register — hör av dig med portfolio, ort och dagsprisnivå.
            </p>
          ),
        },
      ]}
      faq={FAQ}
      closing={{
        title: "Vill du jobba med film hos oss?",
        body: "Skicka portfolio och en kort presentation. Vi svarar på alla ansökningar.",
        primary: { label: "Skicka ansökan", to: "/kontakt" },
        secondary: { label: "Läs om praktik", to: "/praktik" },
      }}
    />
  );
}
